import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';
import { authService } from '../../services/auth.service';
import { useSettingsStore } from '../../stores/settingsStore';

type State =
  | { kind: 'loading' }
  | { kind: 'success'; email: string }
  | { kind: 'already-verified' }
  | { kind: 'error'; message: string; allowResend: boolean }
  | { kind: 'missing' };

/**
 * /email-verified page.
 *
 * Lands here from the verification email we send. Reads the `?token=X`
 * query parameter, POSTs it to /auth/verify-email, shows success or an
 * error with a resend option.
 */
export default function EmailVerified() {
  const [params] = useSearchParams();
  const { general } = useSettingsStore();
  const platformName = `${general?.companyName || 'SecureLink'} VSN+`;

  const [state, setState] = useState<State>({ kind: 'loading' });
  const [resendEmail, setResendEmail] = useState('');
  const [resendStatus, setResendStatus] = useState<'idle' | 'sending' | 'sent'>('idle');

  const token = params.get('token');

  useEffect(() => {
    if (!token) {
      setState({ kind: 'missing' });
      return;
    }
    (async () => {
      try {
        const res = await authService.verifyEmail(token);
        setState({ kind: 'success', email: res.email });
      } catch (err: any) {
        const code = err?.response?.data?.error?.code;
        const msg =
          err?.response?.data?.error?.message ||
          'We could not verify your email with that link.';

        // "Already used" is the benign case — user clicked the link a second
        // time (refresh, second tab, opened from history) after a successful
        // verification, OR an email scanner pre-fetched the URL. The token
        // row exists with `used_at` set, so emailVerified=true was already
        // applied to the KC user. Treat as success and route them to sign in.
        if (/already been used|already used/i.test(msg)) {
          setState({ kind: 'already-verified' });
          return;
        }

        setState({
          kind: 'error',
          message: msg,
          allowResend: code !== 'BAD_REQUEST',
        });
      }
    })();
  }, [token]);

  const handleResend = async () => {
    if (!resendEmail) return;
    setResendStatus('sending');
    try {
      await authService.resendVerification(resendEmail.trim().toLowerCase());
    } finally {
      setResendStatus('sent');
    }
  };

  // ─── Missing token ───────────────────────────────────────────────────
  if (state.kind === 'missing') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-amber-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-amber-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Verification link missing
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            This page expects a verification link from the email we sent.
            Check your inbox for a message from {platformName}, or request a new link below.
          </p>
          <Link
            to="/login"
            className="inline-block w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
          >
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Loading ─────────────────────────────────────────────────────────
  if (state.kind === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="flex items-center gap-2 text-gray-600">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span className="text-sm">Verifying your email…</span>
        </div>
      </div>
    );
  }

  // ─── Error ───────────────────────────────────────────────────────────
  if (state.kind === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-red-50 flex items-center justify-center">
              <AlertTriangle className="w-7 h-7 text-red-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading text-center">
            Verification link unavailable
          </h1>
          <p className="text-sm text-gray-600 mb-6 text-center">{state.message}</p>

          {state.allowResend && resendStatus !== 'sent' && (
            <div className="space-y-3">
              <label className="block text-sm font-medium text-gray-700">
                Send a new verification email
              </label>
              <input
                type="email"
                value={resendEmail}
                onChange={(e) => setResendEmail(e.target.value)}
                className="w-full px-3 py-2.5 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="you@example.com"
                autoComplete="email"
                disabled={resendStatus === 'sending'}
              />
              <button
                onClick={handleResend}
                disabled={!resendEmail || resendStatus === 'sending'}
                className="w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {resendStatus === 'sending' ? 'Sending…' : 'Send new verification email'}
              </button>
            </div>
          )}

          {resendStatus === 'sent' && (
            <p className="text-sm text-emerald-600 text-center mb-4">
              If an account exists, a new verification email has been sent.
            </p>
          )}

          <Link
            to="/login"
            className="block text-center text-sm text-gray-600 hover:text-gray-900 mt-6"
          >
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Already verified (clicked link a second time) ──────────────────
  if (state.kind === 'already-verified') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
          <div className="flex justify-center mb-5">
            <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-600" />
            </div>
          </div>
          <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">
            Already verified
          </h1>
          <p className="text-sm text-gray-600 mb-6">
            Your email is already verified. Sign in to continue to {platformName}.
          </p>
          <Link
            to="/login"
            className="inline-block w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
          >
            Go to sign in
          </Link>
        </div>
      </div>
    );
  }

  // ─── Success ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="max-w-md w-full bg-white rounded-xl shadow-md p-10 text-center">
        <div className="flex justify-center mb-5">
          <div className="w-14 h-14 rounded-full bg-emerald-50 flex items-center justify-center">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
        </div>
        <h1 className="text-xl font-semibold text-gray-900 mb-2 font-heading">Email verified</h1>
        <p className="text-sm text-gray-600 mb-6">
          <strong>{state.email}</strong> is verified. Sign in to continue to {platformName}.
        </p>
        <Link
          to="/login"
          className="inline-block w-full bg-primary-600 text-white py-2.5 rounded-md text-sm font-medium hover:bg-primary-700"
        >
          Go to sign in
        </Link>
      </div>
    </div>
  );
}
